import { Button, EmptyState, Table, TableFooter } from '@orbitalwatch/design';
import type { ColumnDefinition, IOrderInfo, PaginationOptions } from '@orbitalwatch/design';
import { useMemo } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Callout } from './Callout';
import { MOBILE_MEDIA_QUERY, useMediaQuery } from './useMediaQuery';

export interface Column<T> {
    key: string;
    header: ReactNode;
    width?: string;
    render: (item: T) => ReactNode;
    /** Right-aligned, tabular figures (header too). */
    numeric?: boolean;
    align?: 'start' | 'center' | 'end';
    /** Header becomes a sort button; needs `orderBy` / `onOrderChange` on the table. */
    sortable?: boolean;
}

/** 1-based page model; mapped onto the library's 0-based pagination bar. */
export interface Pagination {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
}

export type OrderDirection = 'ASC' | 'DESC';

export interface DataTableProps<T> {
    columns: Column<T>[];
    items: T[] | undefined;
    rowKey: (item: T) => string;
    isLoading?: boolean;
    /** Replaces the rows with the message and, with `onRetry`, a Retry button. */
    error?: unknown;
    onRetry?: () => void;
    emptyText?: string;
    emptyTitle?: string;
    /** Rows are focusable and open with a click or Enter. */
    onRowClick?: (item: T) => void;
    pagination?: Pagination;
    /** Foot line under the table, for a table outside a `Panel` (a `Panel` has its own `footer`). */
    footer?: ReactNode;
    /** Visually hidden `<caption>` (the card list's accessible name on phones). */
    caption?: string;
    orderBy?: { key: string; direction: OrderDirection };
    onOrderChange?: (key: string, direction: OrderDirection | 'NONE') => void;
    /** Below 768px a list of these (library `Card`s) replaces the table. */
    mobileCard?: (item: T) => ReactNode;
}

function describe(error: unknown): string {
    if (error instanceof Error) return error.message;
    return String(error);
}

const noop = () => {};

/**
 * Library `Table` 1.0 (skeleton rows, error + retry, empty state, keyboard-accessible clickable rows, sortable
 * headers, pagination bar) behind the screens' column API; on phones, with `mobileCard`, a card list instead, and
 * without it the table scrolls sideways in its own box.
 */
export function DataTable<T>(props: DataTableProps<T>) {
    const { columns, rowKey, onRowClick, pagination, orderBy, onOrderChange, mobileCard } = props;
    const items = props.items ?? [];
    const isPhone = useMediaQuery(MOBILE_MEDIA_QUERY);
    const errorText = props.error ? describe(props.error) : undefined;
    const emptyText = props.emptyText ?? 'Nothing to show.';

    const columnDefinitions = useMemo((): ColumnDefinition<T>[] => columns.map(column => ({
        columnKey: column.key,
        headerRender: column.header,
        width: column.width,
        cellRenderProp: column.render,
        isSortable: column.sortable,
        align: column.align,
        numeric: column.numeric,
    })), [columns]);

    const hasSortable = columns.some(column => column.sortable);
    const orderKey = orderBy?.key;
    const orderDirection = orderBy?.direction;
    // The library resets its sort state whenever `orderColumns` changes identity, so it is memoised on the values.
    const orderColumns = useMemo(
        (): IOrderInfo[] => (orderKey && orderDirection ? [{ columnKey: orderKey, direction: orderDirection }] : []),
        [orderKey, orderDirection],
    );

    // The library keeps a multi-column order list; the screens sort by one column, the one just clicked.
    const handleOrderChanged = (infos: IOrderInfo[]) => {
        if (!onOrderChange) return;
        const other = infos.find(info => info.columnKey !== orderKey);
        if (other) {
            onOrderChange(other.columnKey, other.direction);
            return;
        }
        const same = infos.find(info => info.columnKey === orderKey);
        if (orderKey) onOrderChange(orderKey, same?.direction ?? 'NONE');
    };

    const paginationOptions: PaginationOptions | undefined = pagination
        ? { itemsPerPage: pagination.pageSize, itemsPerPageOptions: [pagination.pageSize], onItemsPerPageChanged: noop }
        : undefined;

    const footer = props.footer ? <div className="ark-table-foot">{props.footer}</div> : null;

    if (isPhone && mobileCard) {
        let body: ReactNode;
        if (errorText) {
            body = (
                <Callout tone="danger" title={errorText}>
                    {props.onRetry ? <Button type="secondary" size="sm" text="Retry" onClick={props.onRetry} /> : null}
                </Callout>
            );
        } else if (props.isLoading) {
            body = (
                <ul className="ark-card-list" aria-busy="true" aria-label={props.caption}>
                    <li className="ow-sr-only" role="status">Loading…</li>
                    {[0, 1, 2].map(index => (
                        <li key={index} className="ark-card-skeleton" aria-hidden="true">
                            <span className="ow-table-skeleton" style={{ width: '60%' }} />
                            <span className="ow-table-skeleton" style={{ width: '35%' }} />
                        </li>
                    ))}
                </ul>
            );
        } else if (items.length === 0) {
            body = <EmptyState compact className="ark-card-list-empty" title={props.emptyTitle ?? 'No results found'} text={emptyText} />;
        } else {
            body = (
                <ul className="ark-card-list" aria-label={props.caption}>
                    {items.map(item => <li key={rowKey(item)}>{mobileCard(item)}</li>)}
                </ul>
            );
        }
        return (
            <div className="ark-data-table is-cards">
                {body}
                {pagination && !errorText && items.length > 0 ? (
                    <TableFooter
                        as="bar"
                        columnCount={columns.length}
                        currentPage={pagination.page - 1}
                        totalItems={pagination.total}
                        paginationOptions={paginationOptions}
                        onCurrentPageChanged={page => pagination.onPageChange(page + 1)}
                    />
                ) : null}
                {footer}
            </div>
        );
    }

    // Phones without `mobileCard`: every column keeps 8.5rem and the table scrolls in its own box (`ds.css`).
    const style = { '--ark-table-columns': columns.length } as CSSProperties;

    return (
        <div className="ark-data-table" style={style}>
            <Table<T>
                className="ark-table"
                items={items}
                columnDefinitions={columnDefinitions}
                rowKey={rowKey}
                totalItems={pagination?.total ?? items.length}
                isLoading={props.isLoading}
                error={errorText}
                onRetry={props.onRetry}
                emptyMessage={emptyText}
                emptyTitle={props.emptyTitle}
                caption={props.caption}
                // Only the primary button opens a row (the library reports middle / right presses too).
                rowDefinition={onRowClick ? { onClick: (item, button) => { if (!button) onRowClick(item); } } : undefined}
                orderColumns={hasSortable ? orderColumns : undefined}
                onOrderChanged={hasSortable ? handleOrderChanged : undefined}
                currentPage={pagination ? pagination.page - 1 : undefined}
                paginationOptions={paginationOptions}
                onCurrentPageChanged={pagination ? page => pagination.onPageChange(page + 1) : undefined}
            />
            {footer}
        </div>
    );
}
